import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Clock, Calendar, Users, ArrowRight } from 'lucide-react';

const ADCPart2 = () => {
  const examPattern = [
    { 
      title: "Technical Tasks Day", 
      items: [
        "PFM Crown Preparation", 
        "Provisional Crown Preparation", 
        "Class II Composite Restoration", 
        "Class IV Composite Restoration", 
        "Amalgam Restoration", 
        "Class II Cavity Preparation",
        "Endodontic Access Cavity Preparation"
      ] 
    },
    { 
      title: "Skilled OSCEs (Clinical Skills Day)", 
      items: [
        "Radiographs (Bitewing & Periapical)", 
        "Rubber Dam Application", 
        "Sub-gingival Scaling", 
        "Tooth Extraction", 
        "Local Anaesthesia Administration", 
        "Medical Emergencies (CPR, Syncope)", 
        "Pit and Fissure Sealants",
        "Matrix Band and Retainer"
      ] 
    },
    { 
      title: "Infection Control & Safety", 
      items: [
        "Infection Control Protocol", 
        "Sharps Management", 
        "Sterilization Procedures"
      ] 
    }
  ];

  const trainingApproach = [
    "Detailed theoretical discussion for every task",
    "Live demonstrations by expert instructors",
    "In-depth review of ADC rubrics from official booklet",
    "Hands-on practice with personalized support",
    "3-5 simulated mock examinations with strict infection control",
    "Assessment, discussion, and detailed feedback after each mock",
    "Exact ADC exam simulation setup",
    "All necessary written materials provided"
  ];

  const facilities = [
    "Most realistic ADC exam simulation environment",
    "Original manikins (CPR, Local Anesthetic, Radiograph)",
    "Exact ADC exam equipment and setup",
    "Well-equipped dental chairs and units",
    "All OSCE materials and equipment provided",
    "Strict infection control protocols",
    "Modern, spacious training facility",
    "Expert instructors with personalized support"
  ];

  const courseExtensions = [
    "Flexible course extensions for full-course candidates",
    "Additional practice sessions available",
    "Continuous guidance and support",
    "Weekly extensions at nominal fee",
    "Pre-exam review and refinement sessions"
  ];

  const materialsRequired = [
    "Columbia PVR 860 Typodont jaw model",
    "Hand pieces",
    "Drainage mask",
    "Other materials (comprehensive list provided upon registration)"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full mb-4">
              Most Popular Course
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              ADC Part 2 Practical Course
            </h1>
            <p className="text-lg md:text-xl text-gray-100 mb-8">
              Comprehensive 5-week intensive training program for the Australian Dental Council Part 2 Practical Examination
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
                <Link to="/contact">Enroll Now</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/courses">View All Courses</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <Clock className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">35 Days</div>
                <div className="text-sm text-gray-600">30 Working Days</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Mon-Fri</div>
                <div className="text-sm text-gray-600">10 AM - 7 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Saturday</div>
                <div className="text-sm text-gray-600">10 AM - 2 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Users className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Full-time</div>
                <div className="text-sm text-gray-600">Intensive Training</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Exam Pattern */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              ADC Part 2 Exam Pattern
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive coverage of all examination components
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {examPattern.map((section, index) => (
              <Card key={index} className="border-2 hover:border-[#1e3a8a] transition-colors">
                <CardHeader>
                  <CardTitle className="text-xl">{section.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Cover
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive training aligned with ADC Part 2 exam requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">Technical Tasks Day</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">PFM Crown Preparation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Provisional Crown Preparation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Class II and Class IV Composite Restorations</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Amalgam Restoration</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Class II Cavity Preparation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Endodontic Access Cavity Preparation</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600 italic">
                  Each task includes detailed theoretical discussion followed by live demonstrations
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">Skilled OSCEs (Clinical Skills Day)</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Radiographs (Bitewing & Periapical)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Rubber Dam Application</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Sub-gingival Scaling</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Tooth Extraction</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Local Anaesthesia Administration</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Medical Emergencies (CPR, Syncope)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Pit and Fissure Sealants</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Matrix Band and Retainer</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600 italic">
                  Includes Infection Control Protocol and Sharps Management training
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Approach */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Training Approach
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                A structured, comprehensive methodology designed to ensure your success in the ADC Part 2 examination
              </p>
              <ul className="space-y-3">
                {trainingApproach.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#1e3a8a] text-white p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-6">What We Expect From You</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold">1</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Discipline & Daily Practice</div>
                    <div className="text-sm text-gray-200">Consistent effort and dedication to daily training routines</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold">2</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Openness to Feedback</div>
                    <div className="text-sm text-gray-200">Willingness to accept constructive criticism and improve</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold">3</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Strong Commitment</div>
                    <div className="text-sm text-gray-200">Full dedication to the 5-week intensive training program</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              World-Class Facilities
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Most realistic ADC exam simulation environment with original equipment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {facilities.map((facility, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <p className="text-gray-700">{facility}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Extensions & Materials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Course Extensions */}
            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-[#1e3a8a]/20">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">Flexible Course Extensions</h3>
              <p className="text-gray-600 mb-6">
                For full-course candidates, we offer flexible extensions to ensure you feel completely prepared:
              </p>
              <ul className="space-y-3">
                {courseExtensions.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Materials Required */}
            <div className="bg-[#1e3a8a] text-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold mb-6">Materials Required</h3>
              <p className="text-gray-200 mb-6">
                Candidates must bring the following materials:
              </p>
              <ul className="space-y-3 mb-6">
                {materialsRequired.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-sm text-gray-100">
                  📋 Upon registration, you'll receive a comprehensive list of required materials along with guidance on procurement options.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your ADC Part 2 Preparation?
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Join hundreds of successful dentists who cleared ADC Part 2 with ODA training
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
              <Link to="/contact">Enroll Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/courses">View All Courses</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ADCPart2;
